/** @file GuidesDialog: the server's guide topics on the left, the chosen guide's text on the right. */
import { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { Button } from '../ui-kit/Button';
import { Modal } from '../ui-kit/Modal';
import { guideText } from './guideText';
import { guideTopics } from './guideTopics';
import { useApi } from './useApi';

type Loaded<T> = { key: string | null; value: T | null; error: string | null };

/** Runs `load` whenever `key` changes (null = nothing to load); the result carries the key it answers. */
function useLoaded<T>(key: string | null, load: () => Promise<T>): { value: T | null; error: string | null } {
  const [state, setState] = useState<Loaded<T>>({ key: null, value: null, error: null });
  const latest = useRef(load);
  latest.current = load;
  useEffect(() => {
    if (key === null) return undefined;
    let live = true;
    latest.current().then((value) => { if (live) setState({ key, value, error: null }); }, (e: unknown) => {
      if (live) setState({ key, value: null, error: e instanceof Error ? e.message : String(e) });
    });
    return () => { live = false; };
  }, [key]);
  return state.key === key ? { value: state.value, error: state.error } : { value: null, error: null };
}

/** The topic buttons (the picked one highlighted). */
function Topics({ topics, topic, onPick }: { topics: string[]; topic: string | null; onPick: (t: string) => void }): ReactElement {
  return (
    <div className="p3-row" role="tablist" aria-label="Guide topics">
      {topics.map((t) => (
        <Button key={t} role="tab" aria-selected={t === topic} variant={t === topic ? 'primary' : 'default'} onClick={() => { onPick(t); }}>{t}</Button>
      ))}
    </div>
  );
}

/** Topics and the picked guide's text of the server at `url`; the first topic is picked until the human picks one. */
function useGuides(url: string | null, fetchFn: typeof fetch) {
  const [picked, setPicked] = useState<string | null>(null);
  const topics = useLoaded<string[]>(url, () => guideTopics(url ?? '', fetchFn));
  const topic = picked ?? topics.value?.[0] ?? null;
  const text = useLoaded<string>(topic === null ? null : `${url ?? ''}|${topic}`, () => guideText(url ?? '', topic ?? '', fetchFn));
  return { topics: topics.value ?? [], topic, text: text.value ?? '', error: topics.error ?? text.error, setPicked };
}

/** The guide topics of the server the api talks to; picking one shows its markdown as plain text. */
export function GuidesDialog({ onClose, fetchFn = fetch }: { onClose: () => void; fetchFn?: typeof fetch }): ReactElement {
  const url = useApi()?.url ?? null;
  const g = useGuides(url, fetchFn);
  return (
    <Modal title="Guides" onClose={onClose} footer={<Button variant="primary" onClick={onClose}>Close</Button>}>
      {url === null ? <p className="p3-muted">No server: the guides come from the server.</p> : null}
      {g.error === null ? null : <p className="p3-error" role="alert">{g.error}</p>}
      <Topics topics={g.topics} topic={g.topic} onPick={g.setPicked} />
      <pre className="p3-guide" aria-label="Guide text">{g.text}</pre>
    </Modal>
  );
}
